import React, { useEffect, useState } from "react";
import {
  chord,
  createBoard,
  isLost,
  isWon,
  placeMines,
  reveal,
  toggleFlag,
} from "./minesweeperLogic";

const ROWS = 9;
const COLS = 9;
const MINES = 10;
const MAX_COUNTER = 999;

const NUMBER_COLORS = [
  null,
  "#0000ff",
  "#008000",
  "#ff0000",
  "#000080",
  "#800000",
  "#008080",
  "#000000",
  "#808080",
];

// Segments a-g, clockwise from the top, then the middle bar.
const SEGMENTS = {
  a: "M2 1 H10 L8 3 H4 Z",
  b: "M11 2 V10 L9 9 V4 Z",
  c: "M11 12 V20 L9 18 V13 Z",
  d: "M2 21 H10 L8 19 H4 Z",
  e: "M1 12 V20 L3 18 V13 Z",
  f: "M1 2 V10 L3 9 V4 Z",
  g: "M2 11 L4 10 H8 L10 11 L8 12 H4 Z",
};
const DIGIT_SEGMENTS = {
  "0": "abcdef",
  "1": "bc",
  "2": "abdeg",
  "3": "abcdg",
  "4": "bcfg",
  "5": "acdfg",
  "6": "acdefg",
  "7": "abc",
  "8": "abcdefg",
  "9": "abcdfg",
  "-": "g",
};

const Digit = ({ char }) => (
  <svg width="13" height="23" viewBox="0 0 12 22" aria-hidden="true">
    {Object.entries(SEGMENTS).map(([seg, d]) => (
      <path key={seg} d={d} fill={DIGIT_SEGMENTS[char].includes(seg) ? "#ff0000" : "#400000"} />
    ))}
  </svg>
);

const Counter = ({ value }) => {
  const clamped = Math.max(-99, Math.min(MAX_COUNTER, value));
  const text = clamped < 0 ? `-${String(-clamped).padStart(2, "0")}` : String(clamped).padStart(3, "0");
  return (
    <div
      className="flex gap-[1px] p-[2px] bg-black border border-t-[#808080] border-l-[#808080] border-b-white border-r-white"
      aria-label={String(clamped)}
    >
      {[...text].map((c, i) => (
        <Digit key={i} char={c} />
      ))}
    </div>
  );
};

const Face = ({ mood }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
    <circle cx="10" cy="10" r="8.5" fill="#ffff00" stroke="black" />
    {mood === "dead" ? (
      <path d="M5.5 5.5 L8.5 8.5 M8.5 5.5 L5.5 8.5 M11.5 5.5 L14.5 8.5 M14.5 5.5 L11.5 8.5" stroke="black" />
    ) : mood === "cool" ? (
      <path d="M3.5 7 H16.5 M4 7 h5 v2.5 h-5 Z M11 7 h5 v2.5 h-5 Z" stroke="black" fill="black" />
    ) : (
      <>
        <rect x="6" y="6" width="2" height="2" />
        <rect x="12" y="6" width="2" height="2" />
      </>
    )}
    {mood === "surprised" ? (
      <circle cx="10" cy="13.5" r="2" fill="none" stroke="black" />
    ) : mood === "dead" ? (
      <path d="M6 15 Q10 11 14 15" fill="none" stroke="black" />
    ) : (
      <path d="M6 12 Q10 16 14 12" fill="none" stroke="black" />
    )}
  </svg>
);

const MineIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
    <path d="M8 1 V15 M1 8 H15 M3 3 L13 13 M13 3 L3 13" stroke="black" strokeWidth="1.5" />
    <circle cx="8" cy="8" r="4.5" fill="black" />
    <rect x="5.5" y="5.5" width="2" height="2" fill="white" />
  </svg>
);

const FlagIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
    <path d="M9 2 L3 5 L9 8 Z" fill="#ff0000" />
    <path d="M9 2 V11" stroke="black" strokeWidth="1.5" />
    <path d="M5 12 H12 V14 H3 V13 Z" fill="black" />
  </svg>
);

const Cell = ({ cell, gameOver, exploded, onReveal, onFlag }) => {
  const { state, mine, adjacent } = cell;
  const wrongFlag = gameOver && state === "flagged" && !mine;
  const showMine = gameOver && mine && state !== "flagged";

  if (state === "revealed" || showMine || wrongFlag) {
    return (
      <div
        className={`w-6 h-6 flex items-center justify-center border-r border-b border-[#808080] font-bold text-[1.05rem] font-mono ${
          exploded ? "bg-[#ff0000]" : "bg-[#c0c0c0]"
        }`}
        style={{ color: NUMBER_COLORS[adjacent] ?? undefined }}
        onClick={onReveal}
        onContextMenu={(e) => e.preventDefault()}
      >
        {mine || wrongFlag ? (
          <span className="relative">
            <MineIcon />
            {wrongFlag && (
              <svg className="absolute inset-0" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                <path d="M1 1 L15 15 M15 1 L1 15" stroke="#ff0000" strokeWidth="1.5" />
              </svg>
            )}
          </span>
        ) : (
          adjacent > 0 && adjacent
        )}
      </div>
    );
  }

  return (
    <button
      className="w-6 h-6 flex items-center justify-center bg-[#c0c0c0] border-[3px] border-t-white border-l-white border-b-[#808080] border-r-[#808080] active:border-[1px] active:border-t-[#808080] active:border-l-[#808080] active:border-b-transparent active:border-r-transparent"
      aria-label={state === "flagged" ? "Flagged cell" : "Hidden cell"}
      onClick={onReveal}
      onContextMenu={(e) => {
        e.preventDefault();
        onFlag();
      }}
    >
      {state === "flagged" && <FlagIcon />}
    </button>
  );
};

const newGame = () => ({ board: createBoard(ROWS, COLS), started: false, exploded: null });

const Minesweeper = () => {
  const [game, setGame] = useState(newGame);
  const [seconds, setSeconds] = useState(0);
  const [pressing, setPressing] = useState(false);
  const { board, started, exploded } = game;

  const lost = isLost(board);
  const won = started && !lost && isWon(board);
  const over = lost || won;
  const running = started && !over;

  useEffect(() => {
    if (!running) return;
    const timer = setInterval(() => setSeconds((s) => Math.min(MAX_COUNTER, s + 1)), 1000);
    return () => clearInterval(timer);
  }, [running]);

  useEffect(() => {
    const release = () => setPressing(false);
    window.addEventListener("pointerup", release);
    return () => window.removeEventListener("pointerup", release);
  }, []);

  const restart = () => {
    setGame(newGame());
    setSeconds(0);
  };

  const handleReveal = (index) => {
    if (over || board.cells[index].state === "flagged") return;

    let b = board;
    if (!started) {
      b = placeMines(b, MINES, index);
      // Win98 starts the clock at 1 on the first click.
      setSeconds(1);
    }
    const next = b.cells[index].state === "revealed" ? chord(b, index) : reveal(b, index);
    const hit = isLost(next) ? next.cells.findIndex((c) => c.mine && c.state === "revealed") : null;
    setGame({ board: next, started: true, exploded: hit });
  };

  const handleFlag = (index) => {
    if (over) return;
    setGame({ ...game, board: toggleFlag(board, index) });
  };

  const flags = board.cells.filter((c) => c.state === "flagged").length;
  const mood = lost ? "dead" : won ? "cool" : pressing ? "surprised" : "smile";

  return (
    <div className="h-full p-[6px] bg-[#c0c0c0] select-none border-[3px] border-t-white border-l-white border-b-[#808080] border-r-[#808080]">
      {/* Header */}
      <div className="flex items-center justify-between px-[5px] py-[4px] mb-[6px] border-2 border-t-[#808080] border-l-[#808080] border-b-white border-r-white">
        <Counter value={won ? 0 : MINES - flags} />
        <button
          className="w-[26px] h-[26px] flex items-center justify-center bg-[#c0c0c0] border-2 border-t-white border-l-white border-b-[#808080] border-r-[#808080] active:border-t-[#808080] active:border-l-[#808080] active:border-b-white active:border-r-white"
          aria-label="New game"
          title="New game"
          onClick={restart}
        >
          <Face mood={mood} />
        </button>
        <Counter value={seconds} />
      </div>

      {/* Grid */}
      <div
        className="grid w-fit border-[3px] border-t-[#808080] border-l-[#808080] border-b-white border-r-white"
        style={{ gridTemplateColumns: `repeat(${COLS}, 1.5rem)` }}
        onPointerDown={(e) => e.button === 0 && !over && setPressing(true)}
      >
        {board.cells.map((cell, i) => (
          <Cell
            key={i}
            cell={cell}
            gameOver={lost}
            exploded={exploded === i}
            onReveal={() => handleReveal(i)}
            onFlag={() => handleFlag(i)}
          />
        ))}
      </div>
    </div>
  );
};

export default Minesweeper;
